/** Pointer and key helpers shared by the canvas gestures. */

/** A drag, pan or marquee only becomes one past this many screen pixels, at every zoom. */
export const DRAG_THRESHOLD = 3;

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Trap 6, click after a drag: the browser still fires a click when a drag ends
 * over the element it began on. Swallow the next click in the capture phase;
 * a timeout clears the guard when no click comes (release off the element).
 */
export const swallowNextClick = () => {
  const stop = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    window.removeEventListener('click', stop, true);
  };
  window.addEventListener('click', stop, true);
  window.setTimeout(() => window.removeEventListener('click', stop, true), 0);
};

/** Trap 11, keys while typing: an input, select, textarea, contenteditable or anything under `.nokey` keeps its keys. */
export const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  if (el.isContentEditable) return true;
  return !!el.closest('input, select, textarea, [contenteditable=""], [contenteditable="true"], .nokey');
};

export const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Ctrl on Windows and Linux, Cmd on a Mac: the multi-select modifier. */
export const isMultiKey = (e: { ctrlKey: boolean; metaKey: boolean }) => (isMac() ? e.metaKey : e.ctrlKey);

/** setPointerCapture throws on a pointer that already ended; jsdom has no pointer capture at all. */
export const capture = (el: Element | null, pointerId: number) => {
  try {
    el?.setPointerCapture?.(pointerId);
  } catch {
    /* the pointer is gone; nothing to capture */
  }
};
